import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getEmptyParameters, type Parameters, type Template } from './types';

/**
 * Owns the per-template parameter values. Rebuilds the parameter list whenever the selected
 * template changes, prefilling named parameters via `prefill(paramName)`. The prefill callback
 * is read through a ref so the rebuild only re-runs on template identity change (matching the
 * original modals' behaviour).
 */
export default function useTemplateParameters(template: Template | null, prefill: (paramName: string) => string) {
  const [parameters, setParameters] = useState<Parameters>(getEmptyParameters());
  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;

  useEffect(() => {
    if (!template) {
      setParameters(getEmptyParameters());
      return;
    }
    const next = getEmptyParameters();
    template.components.forEach((component) => {
      if (component.type !== 'BUTTONS') {
        const { type, example, text } = component;
        const count = (text?.match(/{{[1-9]}}/g) || []).length;
        for (let i = 0; i < count; i += 1) next[type].push({ text: '', type: 'text' });
        example?.body_text_named_params?.forEach((p) => {
          next[type].push({ text: prefillRef.current(p.param_name), type: 'text', parameter_name: p.param_name });
        });
      } else if (component.buttons) {
        const url =
          component.buttons.length === 2
            ? component.buttons[0].url || component.buttons[1].url || ''
            : component.buttons[0].url || '';
        const count = (url.match(/{{[1-9]}}/g) || []).length;
        for (let i = 0; i < count; i += 1) next.BUTTON.push({ text: '', type: 'text' });
      }
    });
    setParameters(next);
  }, [template?.name]);

  const setParamValue = useCallback((type: keyof Parameters, index: number, value: string) => {
    setParameters((prev) => {
      const updated = [...prev[type]];
      updated[index] = { ...updated[index], text: value };
      return { ...prev, [type]: updated };
    });
  }, []);

  const isComplete = useMemo(
    () =>
      (Object.keys(parameters) as (keyof Parameters)[]).every((key) =>
        parameters[key].every((p) => p.text.trim().length > 0)
      ),
    [parameters]
  );

  return { parameters, setParameters, setParamValue, isComplete };
}
